import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import React, { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthService from "../auth/AuthService";
import env from "../env";

interface Auth0ProviderWithNavigateProps {
  children: ReactNode;
}

export const Auth0ProviderWithNavigate: React.FC<Auth0ProviderWithNavigateProps> = ({ children }) => {
  const navigate = useNavigate();

  const domain = env.AUTH0_DOMAIN.trim();
  const clientId = env.AUTH0_CLIENT_ID.trim();
  const redirectUri = env.AUTH0_CALLBACK_URL.trim();
  const audience = env.AUTH0_AUDIENCE.trim();
  const scope = env.AUTH0_SCOPE.trim();

  const onRedirectCallback = () => {
    const redirectPath = sessionStorage.getItem("redirectPath") || "/";
    sessionStorage.removeItem("redirectPath");
    navigate(redirectPath, { replace: true });
  };

  if (!domain || !clientId || !redirectUri || !audience || !scope) {
    console.error("❌ Auth0 environment variables are missing or incorrectly set.");
    return <div>Error: Authentication configuration is missing.</div>;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience,
        redirect_uri: redirectUri,
        scope,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      <AuthSessionMonitor />
      {children}
    </Auth0Provider>
  );
};

// 🔐 Monitor session + try silent refresh before logout
const AuthSessionMonitor: React.FC = () => {
  const { logout, getIdTokenClaims, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { logout: customLogout } = useAuthService();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const logoutUser = () => {
      customLogout();
      logout({ logoutParams: { returnTo: window.location.origin } });
      localStorage.clear();
      sessionStorage.clear();
    };

    const checkAndScheduleLogout = async () => {
      if (!isAuthenticated) {
        return;
      }

      try {
        const claims = await getIdTokenClaims();
        const exp = claims?.exp;

        if (!exp) {
          return;
        }

        const expiresAt = exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expiresAt - currentTime;
        if (timeUntilExpiry <= 0) {
          try {
            await getAccessTokenSilently();
            checkAndScheduleLogout(); // Reschedule
          } catch {
            logoutUser();
          }
        } else {
          timeoutId = setTimeout(async () => {
         
            try {
              await getAccessTokenSilently();
              checkAndScheduleLogout(); 
            } catch (error) {
              logoutUser();
            }
          }, timeUntilExpiry);
        }
      } catch (error) {
        console.error("Error while checking or refreshing token:", error);
        logoutUser();
      }
    };

    checkAndScheduleLogout();

    return () => clearTimeout(timeoutId);
  }, [getIdTokenClaims, getAccessTokenSilently, isAuthenticated, logout, customLogout]);

  return null;
};
